import { z } from "zod";

// ----- REGEX PATTERNS ----- //
export const regexPatterns = {
  // French phone number: +33 or 0 followed by 9 digits
  phone: /^(\+33|0)[1-9](\d{2}){4}$/,
  // SIRET: exactly 14 digits
  siret: /^[0-9]{14}$/,
  // French postal code: exactly 5 digits
  postalCode: /^[0-9]{5}$/,
  // No special characters except letters, numbers, spaces, hyphens, apostrophes
  noSpecial: /^[A-Za-z0-9\s'-]+$/,
  // Email validation (more comprehensive than basic email)
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
} as const;

// ----- UTILS TO DROP ACCENTS & UPPERCASE ----- //
const normalizeAndUpper = (val: unknown) =>
  typeof val === "string"
    ? val
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
    : val;

// ----- UPPERCASED STRINGS WITHOUT ACCENTS ----- //
const uppercaseString = (maxLength: number, errorMessage: string) =>
  z.preprocess(normalizeAndUpper, z.string().max(maxLength, errorMessage));

// ----- COMMON VALIDATION SCHEMAS ----- //

// SIRET validation with proper format checking
export const siretSchema = z
  .union([
    z.string().regex(regexPatterns.siret, "Le numéro SIRET doit comporter exactement 14 chiffres."),
    z.literal(""),
  ])
  .optional();

// Phone number validation for French numbers
export const phoneSchema = z
  .union([
    z.string().regex(regexPatterns.phone, "Numéro de téléphone invalide. Format attendu: +33XXXXXXXXX ou 0XXXXXXXXX"),
    z.literal(""),
  ])
  .optional();

// Email validation with comprehensive format checking
export const emailSchema = z
  .union([
    z.string()
      .regex(regexPatterns.email, "Adresse email invalide.")
      .max(100, "L'email ne doit pas dépasser 100 caractères."),
    z.literal(""),
  ])
  .optional();

// Postal code validation for French format
export const postalCodeSchema = z.preprocess(
  normalizeAndUpper,
  z.union([
    z.string().regex(regexPatterns.postalCode, "Le code postal doit comporter 5 chiffres."),
    z.literal(""),
  ]).optional(),
);

// Name validation (no special characters, uppercase, no accents)
export const nameSchema = (maxLength: number, fieldName: string) =>
  uppercaseString(maxLength, `${fieldName} ne doit pas dépasser ${maxLength} caractères.`)
    .optional()
    .refine((v) => !v || regexPatterns.noSpecial.test(v), "Caractères invalides, pas d'accents ni de symboles.");

// Address validation (no special characters, uppercase, no accents)
export const addressSchema = (maxLength: number) =>
  uppercaseString(maxLength, `L'adresse ne doit pas dépasser ${maxLength} caractères.`)
    .optional()
    .refine((v) => !v || regexPatterns.noSpecial.test(v), "Caractères invalides, pas d'accents ni de symboles.");

// Required string validation
export const requiredStringSchema = (maxLength: number, fieldName: string) =>
  z.string()
    .min(1, `${fieldName} est requis.`)
    .max(maxLength, `${fieldName} ne doit pas dépasser ${maxLength} caractères.`);

// Optional string validation
export const optionalStringSchema = (maxLength: number, fieldName: string) =>
  z.string().max(maxLength, `${fieldName} ne doit pas dépasser ${maxLength} caractères.`).optional();

// ----- COMMON FORM SCHEMAS ----- //

// Basic contact form schema
export const contactFormSchema = z.object({
  first_name: requiredStringSchema(50, "Le prénom"),
  last_name: requiredStringSchema(50, "Le nom de famille"),
  email: emailSchema,
  phone_number: phoneSchema,
});

// Basic company form schema
export const companyFormSchema = z.object({
  name: requiredStringSchema(100, "La dénomination"),
  siret: siretSchema,
  address: addressSchema(100),
  zip_code: postalCodeSchema,
  city: nameSchema(50, "La ville"),
  phone_number: phoneSchema,
  email: emailSchema,
});

// Search/filter schema
export const searchFormSchema = z.object({
  name: z.string().optional(),
  city: z.string().optional(),
  siret: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;
export type CompanyFormData = z.infer<typeof companyFormSchema>;
export type SearchFormData = z.infer<typeof searchFormSchema>;
